import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { ArrowDown, ArrowUp } from '@/icons/ArrowUpDown';
import ChevronDown from '@/icons/ChevronDown';
import type { MemoriesSortBy, MemoriesSortOrder } from '@/types/memories';
import { defaultMemoriesSortOrder } from '@/types/memories';

interface MemoriesSortControlsProps {
  sortBy: MemoriesSortBy;
  sortOrder: MemoriesSortOrder;
  onSortChange: (sortBy: MemoriesSortBy, sortOrder: MemoriesSortOrder) => void;
}

const MemoriesSortControls: React.FC<MemoriesSortControlsProps> = ({ sortBy, sortOrder, onSortChange }) => {
  const { t } = useTranslation('screens');

  const sortOptions: { value: MemoriesSortBy; label: string }[] = [
    { value: 'createdAt', label: t('filters.sortDateAdded') },
    { value: 'updatedAt', label: t('filters.sortLastUpdated') },
    { value: 'title', label: t('filters.sortTitle') }
  ];

  const directionLabel = sortOrder === 'asc' ? t('filters.sortAscending') : t('filters.sortDescending');

  const handleSortByChange = (nextSortBy: MemoriesSortBy) => {
    onSortChange(nextSortBy, defaultMemoriesSortOrder(nextSortBy));
  };

  const toggleDirection = () => {
    onSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="list-search-row__sort list-sort-controls">
      <span className="list-sort-controls__heading" id="memories-sort-label">
        {t('filters.sort')}
      </span>
      <div className="list-sort-controls__group">
        <Select.Root value={sortBy} onValueChange={(value) => handleSortByChange(value as MemoriesSortBy)}>
          <Select.Trigger className="list-sort-controls__trigger" aria-labelledby="memories-sort-label">
            <Select.Value />
            <Select.Icon className="list-sort-controls__icon" aria-hidden>
              <ChevronDown width={14} height={14} />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              className="list-sort-controls__menu"
              position="popper"
              side="bottom"
              align="end"
              sideOffset={6}
            >
              <Select.Viewport>
                {sortOptions.map(({ value, label }) => (
                  <Select.Item key={value} value={value} className="list-sort-controls__option">
                    <Select.ItemText>{label}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
        <button
          type="button"
          className="list-sort-controls__direction"
          onClick={toggleDirection}
          title={directionLabel}
          aria-label={directionLabel}
        >
          {sortOrder === 'asc' ? <ArrowUp width={16} height={16} /> : <ArrowDown width={16} height={16} />}
        </button>
      </div>
    </div>
  );
};

export default MemoriesSortControls;
